import React, { useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

// ─── Styled components ───────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  padding: 28px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: 'Urbanist', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #171717;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 4px;
  display: flex;
  align-items: center;
  &:hover { color: #111; }
`;

const Reason = styled.textarea`
  width: 100%;
  min-height: 90px;
  padding: 12px 14px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  font-family: 'Urbanist', sans-serif;
  font-size: 15px;
  color: #111;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #C94DAA; }
  &::placeholder { color: #9ca3af; }
`;

const Label = styled.label`
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: block;
  margin-bottom: 6px;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  margin-bottom: 20px;
`;

const Chip = styled.button<{ active?: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid ${p => p.active ? '#C94DAA' : '#e5e7eb'};
  background: ${p => p.active ? 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8), rgba(206, 86, 176, 0.8))' : 'white'};
  color: ${p => p.active ? 'white' : '#374151'};
  font-family: 'Urbanist', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #C94DAA; }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 13px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  background: white;
  font-family: 'Urbanist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`;

const ConfirmBtn = styled.button<{ disabled?: boolean }>`
  flex: 1;
  padding: 13px;
  border-radius: 10px;
  border: none;
  background: ${p => p.disabled ? '#e5e7eb' : 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'};
  font-family: 'Urbanist', sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.disabled ? '#9ca3af' : 'white'};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.9; }
`;

// ─── Common lost reasons ─────────────────────────────────────
const COMMON_REASONS = [
  'Budget mismatch',
  'Chose another studio',
  'No response',
  'Not ready yet',
  'Date conflict',
  'Changed plans',
];

// ─── Component ───────────────────────────────────────────────
interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
}

const MarkLostModal: React.FC<Props> = ({ open, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  const [activeChip, setActiveChip] = useState('');

  if (!open) return null;

  const handleChip = (label: string) => {
    setActiveChip(label);
    setReason(label);
  };

  const handleSubmit = () => {
    if (reason.trim().length < 3) return;
    onConfirm(reason.trim());
  };

  const handleClose = () => {
    setReason('');
    setActiveChip('');
    onClose();
  };

  return (
    <Overlay onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <Modal>
        <ModalHeader>
          <Title>Mark Lead as Lost</Title>
          <CloseBtn onClick={handleClose}><X size={20} /></CloseBtn>
        </ModalHeader>

        <Label>Reason <span style={{ color: '#dc2626' }}>*</span></Label>
        <Reason
          id="lost-reason-input"
          placeholder="e.g. Customer chose another studio"
          value={reason}
          onChange={e => { setReason(e.target.value); setActiveChip(''); }}
        />

        <p style={{ fontFamily: 'Urbanist', fontSize: 13, color: '#6b7280', margin: '8px 0 0' }}>
          Quick select:
        </p>
        <ChipsRow>
          {COMMON_REASONS.map(r => (
            <Chip key={r} active={activeChip === r} onClick={() => handleChip(r)}>
              {r}
            </Chip>
          ))}
        </ChipsRow>

        <ButtonRow>
          <CancelBtn onClick={handleClose}>Cancel</CancelBtn>
          <ConfirmBtn
            id="mark-lost-confirm-btn"
            disabled={reason.trim().length < 3 || loading}
            onClick={handleSubmit}
          >
            {loading ? 'Marking…' : 'Mark as Lost'}
          </ConfirmBtn>
        </ButtonRow>
      </Modal>
    </Overlay>
  );
};

export default MarkLostModal;
